import { Box, Button, useMediaQuery } from "@mui/material";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  contentMaxWidth,
  headerHeight,
  radius,
  ruleHeight,
} from "src/design/tokens";
import { AccountMenu } from "src/grading/components/AccountMenu/AccountMenu";
import { Brand } from "src/grading/components/Brand/Brand";
import { Breadcrumbs } from "src/grading/components/Breadcrumbs/Breadcrumbs";
import { ColorModeToggle } from "src/grading/components/ColorModeToggle/ColorModeToggle";
import LicenseBanner from "src/grading/components/LicenseBanner/LicenseBanner";
import { TopNav } from "src/grading/components/TopNav/TopNav";
import { Footer } from "./Footer";

interface MainLayoutProps {
  children: ReactNode;
}

const NARROW = "@media (max-width: 767.95px)";

/** AppShell: skip link, sticky header with the tricolor rule, the main content column and the footer. */
const MainLayout = ({ children }: MainLayoutProps) => {
  const { t } = useTranslation();
  const compactBrand = useMediaQuery("(max-width: 599.95px)");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Box
        component="a"
        href="#main"
        sx={(theme) => ({
          position: "absolute",
          left: 12,
          top: 12,
          zIndex: theme.zIndex.tooltip + 1,
          // Strings: in `sx`, a number from 0 to 1 means a percentage, so `1` would be 100%.
          width: "1px",
          height: "1px",
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          clipPath: "inset(50%)",
          whiteSpace: "nowrap",
          "&:focus, &:focus-visible": {
            width: "auto",
            height: "auto",
            overflow: "visible",
            clip: "auto",
            clipPath: "none",
            padding: "10px 18px",
            borderRadius: `${radius.pill}px`,
            bgcolor: theme.palette.strong,
            color: theme.palette.onStrong,
            fontWeight: 700,
            fontSize: 14,
            textDecoration: "none",
            outline: `2px solid ${theme.palette.focusRing}`,
            outlineOffset: 2,
          },
        })}
      >
        {t("nav.skipToContent")}
      </Box>
      <Box
        component="header"
        data-print-hide
        sx={(theme) => ({
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar,
          bgcolor: theme.palette.tokens.surface,
          borderBottom: `1px solid ${theme.palette.divider}`,
        })}
      >
        <Box
          sx={{
            maxWidth: contentMaxWidth,
            mx: "auto",
            minHeight: headerHeight,
            padding: "10px 32px",
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
            [NARROW]: { px: 2 },
          }}
        >
          <Brand variant={compactBrand ? "compact" : "full"} />
          <TopNav />
          <Box
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <Button
              variant="outlined"
              href="/station"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: `${radius.pill}px`,
                whiteSpace: "nowrap",
                fontSize: 13,
                fontWeight: 700,
                minHeight: 36,
                px: 1.75,
              }}
            >
              {t("nav.scoreAtStation")}
            </Button>
            <ColorModeToggle variant="switch" />
            <AccountMenu />
          </Box>
        </Box>
        <Box
          data-testid="header-rule"
          aria-hidden
          sx={{ display: "flex", height: ruleHeight }}
        >
          <Box sx={{ flex: 1, bgcolor: "primary.main" }} />
          <Box
            sx={(theme) => ({ flex: 1, bgcolor: theme.palette.tokens.surface })}
          />
          <Box
            sx={(theme) => ({ flex: 1, bgcolor: theme.palette.tokens.danger })}
          />
        </Box>
      </Box>
      <Box
        component="main"
        id="main"
        tabIndex={-1}
        sx={{
          flex: 1,
          width: "100%",
          maxWidth: contentMaxWidth,
          mx: "auto",
          padding: "26px 32px 36px",
          "&:focus": { outline: "none" },
          [NARROW]: { px: 2 },
        }}
      >
        {/* FR-043: every page says when the district's license is suspended. */}
        <LicenseBanner />
        <Breadcrumbs />
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

export default MainLayout;
